/**
 * ASCII Art Generator - Classic ASCII 渲染引擎
 *
 * 实现经典的ASCII字符艺术渲染，支持多种字符集和颜色模式
 * 支持动画渲染
 * Wave 8.3: 支持灰度预计算缓存
 */

import type {
  IRenderEngine,
  FullRenderSettings,
  RenderResult,
  StyleType,
  RGB,
} from '../types'
import type { AnimationContext } from '../animation/types'
import type { RenderContextExt } from './animationHelpers'
import {
  getCharset,
  getCharByBrightness,
} from '../charsets'
import {
  samplePixel,
  samplePixelFast,
  applyContrastAndBrightness,
  applyColorMode,
  invertBrightness,
} from '../algorithms/grayscale'
import {
  calculateCharacterAnimation,
  applyPulseToCharacterBrightness,
  createStaticContext,
  hasGrayscaleCache,
} from './animationHelpers'

/**
 * Classic ASCII 渲染引擎
 * Wave 8.3: 支持灰度预计算缓存优化
 */
export class ClassicEngine implements IRenderEngine {
  readonly name: StyleType = 'classic'

  /**
   * 渲染图片为ASCII艺术（支持动画）
   *
   * @param source 源图像
   * @param settings 渲染设置
   * @param canvas 目标 canvas
   * @param context 可选的动画上下文（包含灰度缓存）
   */
  render(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    settings: FullRenderSettings,
    canvas: HTMLCanvasElement,
    context?: AnimationContext
  ): RenderResult {
    // 使用提供的动画上下文，或创建静态上下文
    const animContext: RenderContextExt = context ?? createStaticContext()

    // 获取源图像数据
    const imageData = this.getImageData(source)
    const imgWidth = imageData.width
    const imgHeight = imageData.height

    // 计算输出尺寸
    const { fontSize, charSpacing, contrast, brightness, invert } = settings
    const charWidth = fontSize * 0.6 * charSpacing
    const charHeight = fontSize

    // 计算输出字符数量
    const cols = Math.max(1, Math.floor(imgWidth / charWidth))
    const rows = Math.max(1, Math.floor(imgHeight / charHeight))

    // 设置画布尺寸
    const outputWidth = cols * charWidth
    const outputHeight = rows * charHeight
    canvas.width = outputWidth
    canvas.height = outputHeight

    const ctx = canvas.getContext('2d')!

    // 填充背景
    ctx.fillStyle = settings.backgroundColor
    ctx.fillRect(0, 0, outputWidth, outputHeight)

    // 设置字体
    ctx.font = `${fontSize}px ${settings.renderFont}`
    ctx.textBaseline = 'top'

    // 获取字符集
    const charset = getCharset(settings.charset, settings.customCharset)

    // 采样区域大小
    const sampleWidth = imgWidth / cols
    const sampleHeight = imgHeight / rows

    // 检查动画或鼠标交互是否启用（鼠标交互可以独立启用）
    const animationEnabled = settings.ditherAnimationEnabled || settings.dither2AnimationEnabled
    const mouseInteractionEnabled = settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled
    const needsAnimation = animationEnabled || mouseInteractionEnabled

    // Wave 8.3: 检查是否有灰度缓存
    const useCache = hasGrayscaleCache(animContext)
    const grayscaleCache = animContext.grayscaleCache

    // 遍历每个字符位置
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        // 计算采样区域
        const sampleX = col * sampleWidth
        const sampleY = row * sampleHeight

        // Wave 8.3: 智能采样 - 使用缓存或原始采样
        const { brightness: rawBrightness, color } = useCache && grayscaleCache
          ? samplePixelFast(grayscaleCache, sampleX, sampleY, sampleWidth, sampleHeight)
          : samplePixel(imageData, sampleX, sampleY, sampleWidth, sampleHeight)

        // 应用对比度和亮度调整（如果使用缓存，对比度亮度已在预计算中处理）
        let finalBrightness = useCache
          ? rawBrightness  // 缓存中已包含对比度亮度调整
          : applyContrastAndBrightness(rawBrightness, contrast, brightness * 255)

        // 应用反转
        if (invert) {
          finalBrightness = invertBrightness(finalBrightness)
        }

        // 计算字符基础位置
        const baseX = col * charWidth
        const baseY = row * charHeight

        // 应用动画或鼠标交互效果
        let drawX = baseX
        let drawY = baseY
        let opacityFactor = 1
        let rainbowColor: { r: number; g: number; b: number } | null = null
        let shouldDraw = true

        if (needsAnimation) {
          const animResult = calculateCharacterAnimation(
            baseX, baseY, animContext, settings, charWidth, charHeight
          )

          drawX += animResult.offsetX
          drawY += animResult.offsetY

          // 以下效果仅在动画启用时应用（鼠标交互不影响）
          if (animationEnabled) {
            // 应用脉动效果到亮度
            finalBrightness = applyPulseToCharacterBrightness(finalBrightness, animResult.pulseFactor)

            // 应用呼吸效果到亮度
            finalBrightness = Math.round(finalBrightness * animResult.brightnessFactor)

            // 保存透明度因子
            opacityFactor = animResult.opacityFactor

            // 保存彩虹颜色
            rainbowColor = animResult.rainbowColor

            // 闪烁可见性
            shouldDraw = animResult.blinkVisible || finalBrightness > 20
          }
        }

        // 跳过不可见的字符
        if (!shouldDraw) continue

        // 获取对应字符
        const char = getCharByBrightness(finalBrightness, charset)

        // 计算最终颜色
        const finalColor = this.getCharacterColor(
          color,
          finalBrightness,
          settings,
          opacityFactor,
          rainbowColor
        )

        // 绘制字符
        ctx.fillStyle = finalColor
        ctx.fillText(char, drawX, drawY)
      }
    }

    return {
      canvas,
      width: outputWidth,
      height: outputHeight,
      charWidth,
      charHeight,
    }
  }

  /**
   * 获取字符颜色
   */
  private getCharacterColor(
    originalColor: RGB,
    _brightness: number,
    settings: FullRenderSettings,
    opacityFactor: number = 1,
    rainbowColor: { r: number; g: number; b: number } | null = null
  ): string {
    const { colorMode, customColor, opacity } = settings

    // 如果有彩虹颜色，优先使用
    if (rainbowColor) {
      const finalOpacity = opacity * opacityFactor
      if (finalOpacity < 1) {
        return `rgba(${rainbowColor.r}, ${rainbowColor.g}, ${rainbowColor.b}, ${finalOpacity.toFixed(2)})`
      }
      return `rgb(${rainbowColor.r}, ${rainbowColor.g}, ${rainbowColor.b})`
    }

    // 应用颜色模式
    const color = applyColorMode(originalColor, colorMode, customColor)

    // 应用透明度（结合动画透明度因子）
    const finalOpacity = opacity * opacityFactor
    if (finalOpacity < 1) {
      return `rgba(${color.r}, ${color.g}, ${color.b}, ${finalOpacity.toFixed(2)})`
    }

    return `rgb(${color.r}, ${color.g}, ${color.b})`
  }

  /**
   * 获取输出尺寸
   */
  getOutputSize(
    imageWidth: number,
    imageHeight: number,
    settings: FullRenderSettings
  ): { width: number; height: number } {
    const { fontSize, charSpacing } = settings
    const charWidth = fontSize * 0.6 * charSpacing
    const charHeight = fontSize

    const cols = Math.max(1, Math.floor(imageWidth / charWidth))
    const rows = Math.max(1, Math.floor(imageHeight / charHeight))

    return {
      width: cols * charWidth,
      height: rows * charHeight,
    }
  }

  /**
   * 从图像源获取ImageData
   */
  private getImageData(
    source: HTMLImageElement | HTMLCanvasElement | ImageData
  ): ImageData {
    if (source instanceof ImageData) {
      return source
    }

    const canvas = document.createElement('canvas')
    canvas.width = source.width
    canvas.height = source.height
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(source, 0, 0)
    return ctx.getImageData(0, 0, canvas.width, canvas.height)
  }
}

// 导出单例实例
export const classicEngine = new ClassicEngine()